/**
 * Dead-session hold — stop a "logged in on screen, no Firebase user" tab from firing its first wave of API calls.
 *
 * ── Why (2026-11-02) ─────────────────────────────────────────────────────────────────────────────
 * localStorage `authenticated` never expires, but the Firebase identity behind it can die (revoked refresh token, a
 * deactivated site's employees removed, browser storage lost) — a ZOMBIE session, healed or flagged by
 * src/services/sessionHealth.js. That guard starts when Layout mounts and only shows a toast, so the tab's first wave of
 * calls (patients list, forms, credit balance…) still goes out tokenless. Backends that enforce the login check refuse it
 * with 401 and Lumigo alerts; for a deactivated site (database dropped) the calls also 500. Seen: Allay Dental Group, 9:17 AM
 * 2026-11-02 — a tab left on a site deactivated on 10-24.
 *
 * ── The rule ─────────────────────────────────────────────────────────────────────────────────────
 * On the FIRST answer from Firebase, if it says "nobody" while the tab believes it is logged in, on a logged-in route, not a
 * demo, with the guard not switched off, and the flag on: HOLD the requests the token policy would tag (same set the session
 * gate holds) until the existing session-health guard has decided:
 *   • healed (silent Microsoft re-auth) → the page reloads; the held requests never went out.
 *   • cannot be healed → the guard signs the tab out to /login (what its "sign in again" toast does on a click); the held
 *     requests never went out.
 *   • anything else (offline, guard off, an error) → RELEASED, exactly as today.
 * Never worse than today: the hold has a hard cap, after which the held requests go out as they always did.
 * A user who IS signed in (every healthy session) never reaches this code — Firebase answers with a user.
 *
 * Pure on purpose, ZERO imports: cypress/node-tests/dead-session-hold.test.js executes this exact file. The wiring lives in
 * src/plugins/authTokenInterceptor.js (begins the hold, the request handler waits on it) and src/plugins/sessionHealthGuard.js
 * (feeds it the guard's verdict, and signs out instead of toasting while it holds).
 */

// Longest a request is held for the guard's verdict, counted from the moment the hold begins. A silent Microsoft re-auth is
// typically 1–3 s; past this the held calls are released as today. Only ever paid by a tab that is already dead.
export const DEAD_SESSION_HOLD_CAP_MS = 5000;

/**
 * Does this look like a dead session that should be held? Every condition must hold.
 *   flagOn            VUE_APP_DEAD_SESSION_GUARD === 'true' (absent ⇒ OFF)
 *   firstAnswer       this is Firebase's FIRST answer of the page load (a later "nobody" is a mid-session sign-out / logout)
 *   authenticated     localStorage `authenticated` === 'true'
 *   authedRoute       the current route requires login (never a public page: forms, booking, login, demo landing)
 *   demoHint          the demo-session hint is set (a demo is never healed or signed out here)
 *   guardOff          the session-health kill switch is 'off' (the guard would never answer)
 */
export function isDeadSessionCandidate(s) {
  const x = s || {};
  return x.flagOn === true && x.firstAnswer === true && x.authenticated === true
    && x.authedRoute === true && x.demoHint !== true && x.guardOff !== true;
}

/**
 * Map a session-health outcome (createSessionHealth.check's resolved string) to what the hold does.
 * 'leaving' = the page is about to reload or sign out → keep holding (the calls must not go out); the cap is the failsafe.
 * 'release' = nothing to wait for → send as today.
 */
export function verdictFor(outcome) {
  const o = typeof outcome === 'string' ? outcome : '';
  if (o.startsWith('healed:') || o.startsWith('prompt:')) return 'leaving';
  return 'release';
}

/**
 * wait()       — resolves when the hold ends (verdict 'release', or the cap) — at once when nothing is held. Never rejects.
 * begin()      — start holding (once per page load). If the guard already decided, applies that verdict immediately.
 * noteOutcome  — the guard's verdict for the boot / auth-null check. Latched, so a verdict that lands before begin() still counts.
 * isHolding / isLeaving — state for the request handler and the guard.
 */
export function createDeadSessionHold({ capMs = DEAD_SESSION_HOLD_CAP_MS, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  let started = false;
  let holding = false;
  let leaving = false;
  let latched = null;
  let timer = null;
  let waiters = [];

  function end() {
    holding = false;
    if (timer !== null) { clearTimer(timer); timer = null; }
    const w = waiters; waiters = [];
    w.forEach((resolve) => resolve());
  }

  function apply(outcome) {
    if (verdictFor(outcome) === 'leaving') leaving = true; // stay held: the page is going away; the cap is the failsafe
    else end();
  }

  return {
    begin() {
      if (started) return;
      started = true;
      holding = true;
      timer = setTimer(end, capMs);
      if (latched !== null) apply(latched);
    },
    noteOutcome(outcome) {
      latched = typeof outcome === 'string' ? outcome : '';
      if (holding) apply(latched);
    },
    isHolding() { return holding; },
    isLeaving() { return holding && leaving; },
    wait() {
      if (!holding) return Promise.resolve();
      return new Promise((resolve) => { waiters.push(resolve); });
    },
  };
}

/** The one hold for this page load — shared by the token interceptor and the session-health guard. */
export const deadSessionHold = createDeadSessionHold();
